import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { financingAPI, clientsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  FileText,
  Upload,
  Check,
  X,
  Clock,
  AlertCircle,
  Download,
  Eye,
  Trash2,
  Plus,
  Search,
  Building2,
  User,
  Loader2,
  CheckCircle,
  XCircle,
  Send,
  RefreshCw,
  FolderOpen,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const FINANCING_STATUSES = [
  { id: 'en_cours', name: 'En cours', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  { id: 'dossier_complet', name: 'Dossier complet', color: 'bg-blue-100 text-blue-700', icon: FolderOpen },
  { id: 'envoye_banque', name: 'Envoyé banque', color: 'bg-purple-100 text-purple-700', icon: Send },
  { id: 'accepte', name: 'Accepté', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { id: 'refuse', name: 'Refusé', color: 'bg-red-100 text-red-700', icon: XCircle },
];

const DOC_STATUS_COLORS = {
  pending: 'bg-slate-100 text-slate-500',
  uploaded: 'bg-blue-100 text-blue-700',
  validated: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const DOC_STATUS_LABELS = {
  pending: 'En attente',
  uploaded: 'Uploadé',
  validated: 'Validé',
  rejected: 'Rejeté',
};

const FinancingPage = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, kanban
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await financingAPI.getRequests();
      setRequests(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocument = async (requestId, docId, file) => {
    setUploadingDoc(docId);
    try {
      await financingAPI.uploadDocument(requestId, docId, file);
      toast.success('Document uploadé');
      fetchRequests();
      if (selectedRequest?.id === requestId) {
        const updated = await financingAPI.getRequest(requestId);
        setSelectedRequest(updated.data);
      }
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleValidateDocument = async (requestId, docId) => {
    try {
      await financingAPI.validateDocument(requestId, docId);
      toast.success('Document validé');
      fetchRequests();
      if (selectedRequest?.id === requestId) {
        const updated = await financingAPI.getRequest(requestId);
        setSelectedRequest(updated.data);
      }
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleRejectDocument = async (requestId, docId) => {
    const reason = prompt('Raison du rejet :');
    if (reason === null) return;
    
    try {
      await financingAPI.rejectDocument(requestId, docId, reason);
      toast.success('Document rejeté');
      fetchRequests();
      if (selectedRequest?.id === requestId) {
        const updated = await financingAPI.getRequest(requestId);
        setSelectedRequest(updated.data);
      }
    } catch (error) {
      toast.error('Erreur lors du rejet');
    }
  };

  const handleToggleDocumentChecked = async (requestId, docId) => {
    try {
      const result = await financingAPI.toggleDocumentChecked(requestId, docId);
      toast.success(result.data.message);
      fetchRequests();
      if (selectedRequest?.id === requestId) {
        const updated = await financingAPI.getRequest(requestId);
        setSelectedRequest(updated.data);
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      await financingAPI.updateRequest(requestId, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchRequests();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const openDetail = async (request) => {
    try {
      const response = await financingAPI.getRequest(request.id);
      setSelectedRequest(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const filteredRequests = requests.filter((r) => {
    const matchesSearch =
      r.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusInfo = (status) => FINANCING_STATUSES.find((s) => s.id === status) || FINANCING_STATUSES[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="financing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Demandes de financement
          </h1>
          <p className="text-slate-500 mt-1">{requests.length} demande(s)</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Tous les statuts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {FINANCING_STATUSES.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Tabs value={view} onValueChange={setView} className="ml-auto">
          <TabsList>
            <TabsTrigger value="list">Liste</TabsTrigger>
            <TabsTrigger value="kanban">Kanban</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* List View */}
      {view === 'list' && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 p-4">Client</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 p-4">Devis</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 p-4">Type</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 p-4">Montant</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 p-4">Documents</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 p-4">Progression</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 p-4">Statut</th>
                    <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 p-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredRequests.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-500">
                        Aucune demande de financement
                      </td>
                    </tr>
                  ) : (
                    filteredRequests.map((request) => {
                      const statusInfo = getStatusInfo(request.status);
                      const docs = request.documents || [];
                      const docsCompleted = docs.filter(d => d.status === 'uploaded' || d.status === 'validated').length;
                      const docsTotal = docs.length;
                      return (
                        <tr key={request.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(request)}>
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                <User className="w-5 h-5 text-slate-500" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-800">{request.client_name}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4" onClick={(e) => e.stopPropagation()}>
                            {request.devis_ids?.length > 0 ? (
                              <div className="space-y-1">
                                {request.devis_ids.slice(0, 2).map((devisId, idx) => (
                                  <Link
                                    key={idx}
                                    to={`/admin/devis/${devisId}`}
                                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    <FileText className="w-3 h-3" />
                                    <span>{request.devis_references?.[idx] || `Devis ${idx + 1}`}</span>
                                  </Link>
                                ))}
                                {request.devis_ids.length > 2 && (
                                  <span className="text-xs text-slate-500">+{request.devis_ids.length - 2} autre(s)</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="p-4">
                            <Badge variant="outline" className="capitalize">
                              {request.client_type === 'eurl_sas_sarl' ? 'EURL/SAS/SARL' :
                               request.client_type === 'entreprise_individuelle' ? 'Ent. Individuelle' :
                               request.client_type === 'agriculteur' ? 'Agriculteur' :
                               request.client_type}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <p className="font-mono font-semibold text-slate-800">
                              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(request.amount || 0)}
                            </p>
                          </td>
                          <td className="p-4">
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {docs.slice(0, 6).map((doc, idx) => (
                                <div
                                  key={idx}
                                  title={doc.name}
                                  className={cn(
                                    "w-5 h-5 rounded border flex items-center justify-center text-xs",
                                    doc.status === 'validated' ? 'bg-green-100 border-green-300 text-green-700' :
                                    doc.status === 'uploaded' ? 'bg-blue-100 border-blue-300 text-blue-700' :
                                    doc.status === 'rejected' ? 'bg-red-100 border-red-300 text-red-700' :
                                    'bg-slate-100 border-slate-300 text-slate-400'
                                  )}
                                >
                                  {doc.status === 'validated' ? '✓' :
                                   doc.status === 'uploaded' ? '↑' :
                                   doc.status === 'rejected' ? '✗' : '○'}
                                </div>
                              ))}
                              {docs.length > 6 && (
                                <span className="text-xs text-slate-500">+{docs.length - 6}</span>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <Progress value={request.progress || 0} className="w-24 h-2" />
                              <span className="text-sm font-medium text-slate-600">{docsCompleted}/{docsTotal}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge className={statusInfo.color}>
                              {statusInfo.name}
                            </Badge>
                          </td>
                          <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDetail(request)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Voir
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Kanban View */}
      {view === 'kanban' && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {FINANCING_STATUSES.map((status) => {
            const columnRequests = filteredRequests.filter((r) => r.status === status.id);
            const StatusIcon = status.icon;
            
            return (
              <div key={status.id} className="flex-shrink-0 w-80">
                <div className={cn('rounded-t-lg p-3', status.color)}>
                  <div className="flex items-center gap-2">
                    <StatusIcon className="w-4 h-4" />
                    <span className="font-semibold">{status.name}</span>
                    <Badge variant="secondary" className="ml-auto">{columnRequests.length}</Badge>
                  </div>
                </div>
                <div className="bg-slate-100 rounded-b-lg p-2 min-h-[400px] space-y-2">
                  {columnRequests.map((request) => (
                    <Card
                      key={request.id}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => openDetail(request)}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between mb-2">
                          <p className="font-medium text-slate-800">{request.client_name}</p>
                          <Badge variant="outline" className="text-xs capitalize">
                            {request.client_type}
                          </Badge>
                        </div>
                        
                        <div className="mb-3">
                          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                            <span>Progression</span>
                            <span>{request.progress || 0}%</span>
                          </div>
                          <Progress value={request.progress || 0} className="h-1.5" />
                        </div>
                        
                        {/* Documents status */}
                        <div className="flex flex-wrap gap-1">
                          {request.documents?.slice(0, 4).map((doc) => (
                            <div
                              key={doc.doc_id}
                              className={cn(
                                'w-6 h-6 rounded flex items-center justify-center text-xs',
                                doc.status === 'validated' ? 'bg-green-100 text-green-600' :
                                doc.status === 'uploaded' ? 'bg-blue-100 text-blue-600' :
                                doc.status === 'rejected' ? 'bg-red-100 text-red-600' :
                                'bg-slate-100 text-slate-400'
                              )}
                              title={`${doc.name}: ${DOC_STATUS_LABELS[doc.status]}`}
                            >
                              {doc.status === 'validated' ? <Check className="w-3 h-3" /> :
                               doc.status === 'rejected' ? <X className="w-3 h-3" /> :
                               doc.status === 'uploaded' ? <Upload className="w-3 h-3" /> :
                               <Clock className="w-3 h-3" />}
                            </div>
                          ))}
                          {request.documents?.length > 4 && (
                            <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center text-xs text-slate-500">
                              +{request.documents.length - 4}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-purple-600" />
              Demande de financement - {selectedRequest?.client_name}
            </DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-6">
              {/* Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Type de client</p>
                  <p className="font-medium capitalize">{selectedRequest.client_type}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Montant</p>
                  <p className="font-mono font-semibold">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(selectedRequest.amount || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Progression</p>
                  <div className="flex items-center gap-2">
                    <Progress value={selectedRequest.progress || 0} className="flex-1 h-2" />
                    <span className="font-semibold">{selectedRequest.progress || 0}%</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Devis lié(s)</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedRequest.devis_ids?.length > 0 ? (
                      selectedRequest.devis_ids.map((devisId, idx) => (
                        <Link
                          key={idx}
                          to={`/admin/devis/${devisId}`}
                          onClick={() => setDetailDialogOpen(false)}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm hover:bg-blue-100 transition-colors"
                        >
                          <FileText className="w-3 h-3" />
                          {selectedRequest.devis_references?.[idx] || `Devis ${idx + 1}`}
                        </Link>
                      ))
                    ) : (
                      <span className="text-slate-400 text-sm">Aucun devis lié</span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-1">Statut</p>
                  <Select
                    value={selectedRequest.status}
                    onValueChange={(val) => handleUpdateStatus(selectedRequest.id, val)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FINANCING_STATUSES.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Pièces justificatives
                </h3>
                <div className="space-y-2">
                  {selectedRequest.documents?.map((doc) => (
                    <div
                      key={doc.doc_id}
                      className={cn(
                        'flex items-center justify-between p-3 rounded-lg border',
                        doc.status === 'validated' ? 'border-green-200 bg-green-50' :
                        doc.status === 'rejected' ? 'border-red-200 bg-red-50' :
                        doc.status === 'uploaded' ? 'border-blue-200 bg-blue-50' :
                        'border-slate-200 bg-white'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {/* Case à cocher cliquable */}
                        <div className="flex items-center">
                          <Checkbox
                            id={`doc-${doc.doc_id}`}
                            checked={doc.status === 'validated' || doc.status === 'uploaded'}
                            onCheckedChange={() => handleToggleDocumentChecked(selectedRequest.id, doc.doc_id)}
                            className={cn(
                              'w-6 h-6 border-2',
                              doc.status === 'validated' ? 'border-green-500 data-[state=checked]:bg-green-500' :
                              doc.status === 'uploaded' ? 'border-blue-500 data-[state=checked]:bg-blue-500' :
                              doc.status === 'rejected' ? 'border-red-500' :
                              'border-slate-300'
                            )}
                          />
                        </div>
                        <div className="flex-1">
                          <label 
                            htmlFor={`doc-${doc.doc_id}`}
                            className="font-medium text-slate-800 cursor-pointer"
                          >
                            {doc.name}
                            {doc.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          <p className="text-xs text-slate-500">
                            {DOC_STATUS_LABELS[doc.status]}
                            {doc.manually_checked && ' (reçu en main propre)'}
                            {doc.file_name && ` - ${doc.file_name}`}
                            {doc.rejection_reason && ` (${doc.rejection_reason})`}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        {/* Upload button for pending or rejected docs */}
                        {(doc.status === 'pending' || doc.status === 'rejected') && (
                          <>
                            <input
                              type="file"
                              id={`file-${doc.doc_id}`}
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handleUploadDocument(selectedRequest.id, doc.doc_id, e.target.files[0]);
                                }
                              }}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => document.getElementById(`file-${doc.doc_id}`)?.click()}
                              disabled={uploadingDoc === doc.doc_id}
                              title="Uploader un fichier"
                            >
                              {uploadingDoc === doc.doc_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Upload className="w-4 h-4" />
                              )}
                            </Button>
                          </>
                        )}
                        
                        {/* Validate/Reject buttons for uploaded docs */}
                        {doc.status === 'uploaded' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => handleValidateDocument(selectedRequest.id, doc.doc_id)}
                              title="Valider le document"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleRejectDocument(selectedRequest.id, doc.doc_id)}
                              title="Rejeter le document"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}

                        {doc.file_path && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                const response = await financingAPI.downloadDocument(selectedRequest.id, doc.doc_id);
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = doc.file_name || `${doc.doc_id}.pdf`;
                                link.click();
                              } catch (e) {
                                toast.error('Erreur lors du téléchargement');
                              }
                            }}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Banques */}
              {selectedRequest.banques?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Banques</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedRequest.banques.map((b) => (
                      <Badge key={b.id} variant="outline">
                        {b.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancingPage;
